
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
import { ShoninCommentHojoModel } from "@/interfaces/shinsei.interface";
import {
  checkShoninCommentHojoSeq,
  createShoninCommentHojo,
  getShoninCommentHojoById,
  updateShoninCommentHojo,
} from "@/api/shinsei.api";
import { getShoninCommentBunruiList } from "@/api/masterTable.api";

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      id: 0,
      seq: 0,
      bunruiCd: "",
      shoninComment: "",
    } as ShoninCommentHojoModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      seq: [
        {
          required: true,
          validator: async (self: any, value: string, callback: any) => {
            if (!value) {
              callback(CheckMsg.CHK_SHONIN_COMMENT_SEQ);
            } else if (/\D/g.test(value)) {
              callback(CheckMsg.CHK_ISNUM);
            } else {
              try {
                await checkShoninCommentHojoSeq({
                  id: rowId.value,
                  databaseField: "seq",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "SEQ",
        },
      ],
      bunruiCd: [
        {
          required: true,
          message: CheckMsg.CHK_SHONIN_COMMENT_BUNRUI,
          trigger: "blur",
        },
      ],
      shoninComment: [
        {
          required: true,
          message: CheckMsg.CHK_SHONIN_COMMENT,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            if (props.operation === DialogGenre.Add) {
              await createShoninCommentHojo({
                seq: form.seq,
                bunruiCd: form.bunruiCd,
                shoninComment: form.shoninComment,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SHONIN_COMMENT_ADDED,
              });
            } else {
              await updateShoninCommentHojo(form.id, {
                bunruiCd: form.bunruiCd,
                shoninComment: form.shoninComment,
              });

              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SHONIN_COMMENT_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getShoninCommentHojoInfo = async (id: number) => {
      const {
        data: { result },
      } = await getShoninCommentHojoById(id);
      form.id = result.id;
      form.seq = +result.seq;
      form.bunruiCd = result.bunrui.bunrui_cd;
      form.shoninComment = result.shonin_comment;
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getShoninCommentHojoInfo(rowId.value as number);
      }
    });
    //承認コメント分類リスト初期化
    const bunruiList = await getShoninCommentBunruiList();

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
      bunruiList,
    };
  },
});
